<template>
  <span class="construct-visual-collection">
    <span v-if="!displayItems.length" :class="textClass" :style="textStyle">{{ emptyText }}</span>

    <span v-else-if="hasItemVisual && layout === 'inline'" class="d-inline-flex flex-wrap align-center">
      <template v-for="(entry, index) in displayItems">
        <span :key="`visual-${index}`" class="d-inline-flex align-center" :class="entryActionClass(entry)" @click="activateEntry(entry, $event)">
          <Renderer :content="item.content" :line="line" :page="page" :itemData="entry.value" :actionResolver="actionResolver" @action="$emit('action', $event)" />
        </span>
        <span v-if="index < displayItems.length - 1" :key="`visual-separator-${index}`">{{ displaySeparator }}</span>
      </template>
    </span>

    <span v-else-if="hasItemVisual && layout === 'stack'" class="d-block">
      <span v-for="(entry, index) in displayItems" :key="index" class="d-block" :class="entryActionClass(entry)" @click="activateEntry(entry, $event)">
        <Renderer :content="item.content" :line="line" :page="page" :itemData="entry.value" :actionResolver="actionResolver" @action="$emit('action', $event)" />
      </span>
    </span>

    <span v-else-if="hasItemVisual && layout === 'chips'" class="d-inline-flex flex-wrap align-center">
      <v-chip
        v-for="(entry, index) in displayItems"
        :key="index"
        :color="entry.chipColor || undefined"
        :small="item.chipSmall !== false"
        :outlined="item.chipOutlined !== false"
        class="mr-1 mb-1" :class="entryActionClass(entry)" v-on="entryActionListeners(entry)"
      >
        <Renderer :content="item.content" :line="line" :page="page" :itemData="entry.value" :actionResolver="actionResolver" @action="$emit('action', $event)" />
      </v-chip>
    </span>

    <span v-else-if="hasItemVisual" :class="textClass" :style="textStyle">
      <span v-for="(entry, index) in displayItems" :key="index" class="d-block construct-visual-list-item" :class="entryActionClass(entry)" @click="activateEntry(entry, $event)">
        <Renderer :content="item.content" :line="line" :page="page" :itemData="entry.value" :actionResolver="actionResolver" @action="$emit('action', $event)" />
      </span>
    </span>

    <span v-else-if="layout === 'inline'" :class="textClass" :style="textStyle">
      <template v-for="(entry, index) in displayItems">
        <span :key="`item-${index}`" :class="entryActionClass(entry)" @click="activateEntry(entry, $event)">{{ entry.text }}</span><span v-if="index < displayItems.length - 1" :key="`separator-${index}`">{{ displaySeparator }}</span>
      </template>
    </span>

    <span v-else-if="layout === 'stack'" :class="textClass" :style="textStyle">
      <span v-for="(entry, index) in displayItems" :key="index" class="d-block" :class="entryActionClass(entry)" @click="activateEntry(entry, $event)">{{ entry.text }}</span>
    </span>

    <span v-else-if="layout === 'chips'" class="d-inline-flex flex-wrap align-center">
      <v-chip
        v-for="(entry, index) in displayItems"
        :key="index"
        :color="entry.chipColor || undefined"
        :small="item.chipSmall !== false"
        :outlined="item.chipOutlined !== false"
        class="mr-1 mb-1" :class="entryActionClass(entry)" v-on="entryActionListeners(entry)"
      >
        {{ entry.text }}
      </v-chip>
    </span>

    <span v-else :class="textClass" :style="textStyle">
      <span v-for="(entry, index) in displayItems" :key="index" class="d-block construct-visual-list-item" :class="entryActionClass(entry)" @click="activateEntry(entry, $event)">{{ entry.text }}</span>
    </span>

    <span v-if="remainingCount > 0 && item.showRemainingCount !== false" :class="[textClass, 'd-block construct-visual-remaining']" :style="textStyle">
      {{ remainingText }}
    </span>
  </span>
</template>

<script>
import displayMixin from './mixin'

export default {
  name: 'ConstructVisualDisplayCollection',
  mixins: [displayMixin],
  components: { Renderer: () => import('./Renderer.vue') },
  computed: {
    rawValue() { return this.resolveCollectionSource(this.item.value) },
    values() { return this.normalizeStructuredValues(this.normalizeValues(this.rawValue)) },
    allDisplayItems() {
      return this.values
        .map((value, index) => ({
          text: this.formatItem(value, index),
          chipColor: this.formatItemTemplate(this.item.chipColor, value, index),
          value: this.itemValue(value, index),
        }))
        .filter(entry => this.hasItemVisual || entry.text !== '')
    },
    hasItemVisual() { return Array.isArray(this.item.content) && this.item.content.length > 0 },
    displayLimit() {
      const limit = parseInt(this.item.limit, 10)
      return Number.isFinite(limit) && limit > 0 ? limit : 0
    },
    displayItems() {
      return this.displayLimit > 0 ? this.allDisplayItems.slice(0, this.displayLimit) : this.allDisplayItems
    },
    remainingCount() { return Math.max(0, this.allDisplayItems.length - this.displayItems.length) },
    remainingText() {
      const template = this.item.remainingText === undefined ? '+ {count} lignes' : this.item.remainingText
      return this.resolveTemplate(template, '').replace(/\{count\}/g, String(this.remainingCount))
    },
    layout() {
      return ['inline', 'stack', 'chips', 'bullets'].includes(this.item.layout)
        ? this.item.layout
        : 'inline'
    },
    displaySeparator() {
      return this.item.displaySeparator === undefined ? ', ' : String(this.item.displaySeparator)
    },
    emptyText() { return this.resolveTemplate(this.item.emptyText, '') },
    textClass() { return this.item.size || 'body-2' },
    textStyle() {
      return { color: this.resolveValue(this.item.color, this.item.colorSource) || undefined }
    },
  },
  methods: {
    entryActionState(entry) {
      if (!this.actionResolver || !this.item.key) return { matched: false, enabled: false }
      return this.actionResolver({ key: this.item.key, line: this.line, itemData: entry.value }) || { matched: false, enabled: false }
    },
    entryActionClass(entry) {
      const state = this.entryActionState(entry)
      return { 'construct-visual-entry-action': state.matched && state.enabled, 'construct-visual-entry-blocked': state.matched && !state.enabled }
    },
    entryActionListeners(entry) {
      return this.entryActionState(entry).matched ? { click: event => this.activateEntry(entry, event) } : {}
    },
    activateEntry(entry, event) {
      if (!this.actionResolver) return
      if (event.target && event.target.closest && event.target.closest('a[href], button, input, select, textarea')) {
        event.stopPropagation()
        return
      }
      const state = this.entryActionState(entry)
      if (!state.matched) return
      event.stopPropagation()
      if (state.enabled) this.$emit('action', { key: this.item.key, line: this.line, itemData: entry.value, event })
    },
    resolveCollectionSource(value) {
      if (typeof value !== 'string') return value
      const directVariable = value.trim().match(/^\[\[(table|item)\|\|([^\]]+)\]\]$/)
      if (!directVariable) return this.resolveTemplate(value, '')
      return this.pathValue(directVariable[1] === 'item' ? this.itemData : this.line, directVariable[2])
    },
    pathValue(source, path) {
      if (source === null || source === undefined) return undefined
      if (Object.prototype.hasOwnProperty.call(Object(source), path)) return source[path]
      return String(path).split(/[. ]/).filter(Boolean).reduce((value, key) => (
        value !== null && value !== undefined ? value[key] : undefined
      ), source)
    },
    parseJsonArray(value) {
      if (typeof value !== 'string' || !value.trim().startsWith('[')) return null
      try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed : null
      } catch (error) {
        return null
      }
    },
    normalizeValues(value) {
      if (Array.isArray(value)) return value
      if (value === null || value === undefined || value === '' || value === false) return []

      const jsonArray = this.parseJsonArray(value)
      if (jsonArray) return jsonArray

      const sourceType = this.item.sourceType || 'auto'
      if (sourceType === 'delimited' || sourceType === 'auto') {
        const delimiter = this.item.delimiter === undefined ? ',' : String(this.item.delimiter)
        if (delimiter) {
          return String(value).split(delimiter).map(entry => entry.trim()).filter(entry => entry !== '')
        }
      }

      return [value]
    },
    normalizeStructuredValues(values) {
      const itemDelimiter = this.item.itemDelimiter === undefined ? '' : String(this.item.itemDelimiter)
      if (!itemDelimiter) return values

      const keys = Array.isArray(this.item.itemKeys)
        ? this.item.itemKeys.map(key => String(key).trim())
        : []

      return values.map(value => {
        if (value !== null && typeof value === 'object') return value
        const parts = String(value).split(itemDelimiter).map(part => part.trim())
        if (!keys.length) return parts
        return keys.reduce((entry, key, index) => {
          if (key) entry[key] = parts[index] === undefined ? '' : parts[index]
          return entry
        }, {})
      })
    },
    itemValue(value, index) {
      if (value !== null && typeof value === 'object') return { ...value, __index: index }
      return { value, __index: index }
    },
    formatItem(value, index) {
      if (this.item.itemTemplate) return this.formatItemTemplate(this.item.itemTemplate, value, index)
      if (value === null || value === undefined) return ''
      if (typeof value !== 'object') return String(value)

      const preferredValue = ['nom', 'name', 'designation']
        .map(key => value[key])
        .find(entry => entry !== undefined && entry !== null)
      if (preferredValue !== undefined) return String(preferredValue)

      try {
        return JSON.stringify(value)
      } catch (error) {
        return String(value)
      }
    },
    formatItemTemplate(template, value, index) {
      if (template === null || template === undefined) return ''
      const withItemValues = String(template).replace(/\[\[item\|\|([^\]]+)\]\]/g, (match, path) => {
        if (path === 'value') return value === null || value === undefined ? '' : String(value)
        if (path === 'index') return String(index)
        const found = this.pathValue(value, path)
        return found === null || found === undefined ? '' : String(found)
      })
      return this.resolveTemplate(withItemValues, '')
    },
  },
}
</script>

<style scoped>
.construct-visual-list-item::before {
  content: '•';
  display: inline-block;
  margin-right: 0.45em;
}
.construct-visual-remaining {
  opacity: 0.72;
  font-style: italic;
}
.construct-visual-entry-action { cursor: pointer; }
.construct-visual-entry-blocked { cursor: default; }
</style>
